import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toPng, toBlob } from "html-to-image";
import api from "../lib/api";
import { formatIQD, formatDate, daysAgo, todayISO } from "../lib/format";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import { Search, Send, Image as ImageIcon, Copy } from "lucide-react";

export default function OutstandingPayments() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [age, setAge] = useState("all");
  const [sort, setSort] = useState("balance_desc");
  const [selected, setSelected] = useState(new Set());
  const [exporting, setExporting] = useState(false);
  const [copying, setCopying] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [excluded, setExcluded] = useState(new Set());
  const [threshold, setThreshold] = useState(0);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [lastRun, setLastRun] = useState(null);
  const exportRef = useRef(null);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/balances", { params: { only_outstanding: true, q } }).then((r) => setRows(r.data));
  }, [q]);

  const filtered = useMemo(() => {
    let r = rows;
    if (age !== "all") {
      const min = { "7": 7, "15": 15, "30": 30 }[age] || 0;
      r = r.filter((x) => (daysAgo(x.oldest_unpaid_date) || 0) > min);
    }
    return [...r].sort((a, b) => {
      if (sort === "balance_desc") return b.outstanding - a.outstanding;
      if (sort === "balance_asc") return a.outstanding - b.outstanding;
      if (sort === "oldest") return (daysAgo(b.oldest_unpaid_date) || 0) - (daysAgo(a.oldest_unpaid_date) || 0);
      return 0;
    });
  }, [rows, sort, age]);

  const total = filtered.reduce((s, r) => s + r.outstanding, 0);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const selectAll = () => setSelected(new Set(filtered.map((r) => r.id)));
  const clearSel = () => setSelected(new Set());

  const selectedRows = filtered.filter((r) => selected.has(r.id));
  const selectedTotal = selectedRows.reduce((s, r) => s + r.outstanding, 0);

  const exportImage = async () => {
    if (selectedRows.length === 0) return toast.error("Select at least one employee");
    setExporting(true);
    try {
      await new Promise((r) => setTimeout(r, 50));
      const node = exportRef.current;
      if (!node) throw new Error("Export area not ready");
      const dataUrl = await toPng(node, { pixelRatio: 2, backgroundColor: "#ffffff", cacheBust: true, skipFonts: true });
      const link = document.createElement("a");
      link.download = `outstanding-payments-${todayISO()}.png`;
      link.href = dataUrl;
      link.click();
      toast.success(`Exported ${selectedRows.length} employee${selectedRows.length !== 1 ? "s" : ""} as PNG`);
    } catch (e) {
      toast.error(e.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const copyImage = async () => {
    if (selectedRows.length === 0) return toast.error("Select at least one employee");
    if (!navigator.clipboard || !window.ClipboardItem) {
      return toast.error("Clipboard image copy is not supported in this browser");
    }
    setCopying(true);
    try {
      await new Promise((r) => setTimeout(r, 50));
      const node = exportRef.current;
      if (!node) throw new Error("Export area not ready");
      const blob = await toBlob(node, { pixelRatio: 2, backgroundColor: "#ffffff", cacheBust: true, skipFonts: true });
      if (!blob) throw new Error("Failed to generate image");
      await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
      toast.success(`Image copied — paste into WhatsApp Web with ⌘/Ctrl+V`);
    } catch (e) {
      toast.error(e.message || "Copy failed");
    } finally {
      setCopying(false);
    }
  };

  // Reminder preview
  const openPreview = async () => {
    setPreviewLoading(true);
    setPreviewOpen(true);
    try {
      const { data } = await api.get("/reminders/preview");
      setCandidates(data.candidates || []);
      setThreshold(data.threshold);
      const noPhone = new Set((data.candidates || []).filter((c) => !c.phone).map((c) => c.employee_id));
      setExcluded(noPhone);
    } catch (e) {
      toast.error("Failed to load preview");
    } finally {
      setPreviewLoading(false);
    }
  };
  const toggleExcluded = (id) => {
    const next = new Set(excluded);
    if (next.has(id)) next.delete(id); else next.add(id);
    setExcluded(next);
  };
  const sendReminders = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/reminders/send-now", { exclude_ids: Array.from(excluded) });
      setLastRun(data);
      setPreviewOpen(false);
      const truly_sent = data.results.filter((r) => r.ok).length;
      const failed = data.results.filter((r) => !r.ok && r.error !== "Excluded by admin").length;
      if (truly_sent > 0) toast.success(`WhatsApp reminders sent to ${truly_sent} employee${truly_sent !== 1 ? "s" : ""}`);
      if (failed > 0) toast.warning(`${failed} failed (missing phone or Twilio error)`);
      if (truly_sent === 0 && failed === 0) toast.info("No employees above the reminder threshold");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send reminders");
    } finally {
      setSending(false);
    }
  };
  const willSendCount = candidates.length - excluded.size;

  return (
    <div className="space-y-6" data-testid="outstanding-page">
      <div className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <h1 className="heading text-2xl font-extrabold tracking-tight">Outstanding Payments</h1>
          <p className="text-sm text-muted-foreground">Employees who currently owe money. Select rows to export as an image or send WhatsApp reminders.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Card className="p-4">
            <div className="text-xs uppercase text-muted-foreground">Total Outstanding</div>
            <div className="heading text-2xl font-extrabold text-rose-700" data-testid="out-total">{formatIQD(total)}</div>
          </Card>
          <Button className="bg-teal-600 hover:bg-teal-700" data-testid="btn-send-reminders" onClick={openPreview}>
            <Send size={14} className="mr-1" />Send WhatsApp Reminders
          </Button>
        </div>
      </div>

      {lastRun && (
        <Card className="p-4 bg-teal-50 dark:bg-teal-950/30 border-teal-200 dark:border-teal-900" data-testid="reminder-summary">
          <div className="text-sm">
            Last run: <strong>{lastRun.sent}</strong> sent · <strong>{lastRun.skipped}</strong> skipped · <strong>{lastRun.total_candidates}</strong> above threshold
          </div>
          <div className="mt-1 text-xs text-muted-foreground">View full history in <a href="/reminder-log" className="text-teal-700 hover:underline">Reminder Log</a>.</div>
        </Card>
      )}

      <Card className="p-4">
        <div className="flex gap-3 flex-wrap items-end">
          <div className="relative flex-1 min-w-56">
            <Search size={14} className="absolute left-2.5 top-2.5 text-muted-foreground" />
            <Input placeholder="Search" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8" data-testid="out-search" />
          </div>
          <Select value={age} onValueChange={setAge}>
            <SelectTrigger className="w-56" data-testid="out-age"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="7">Older than 7 days</SelectItem>
              <SelectItem value="15">Older than 15 days</SelectItem>
              <SelectItem value="30">Older than 30 days</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-56" data-testid="out-sort"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="balance_desc">Highest balance</SelectItem>
              <SelectItem value="balance_asc">Lowest balance</SelectItem>
              <SelectItem value="oldest">Oldest unpaid</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Selection toolbar */}
      <div className="flex items-center justify-between flex-wrap gap-3 rounded-xl bg-slate-900 text-white p-4" data-testid="selection-bar">
        <div className="flex gap-6 items-center flex-wrap">
          <div>
            <div className="text-[11px] uppercase text-slate-400 tracking-wider">Selected</div>
            <div className="heading text-2xl font-extrabold" data-testid="sel-count">{selectedRows.length}</div>
          </div>
          <div>
            <div className="text-[11px] uppercase text-amber-300 tracking-wider">Selected Outstanding</div>
            <div className="heading text-2xl font-extrabold text-amber-300" data-testid="sel-total">{formatIQD(selectedTotal)}</div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={selectAll} className="bg-white/10 border-white/20 text-white hover:bg-white/20" data-testid="btn-select-all-out">Select All</Button>
          <Button variant="outline" onClick={clearSel} className="bg-white/10 border-white/20 text-white hover:bg-white/20" data-testid="btn-clear-sel">Clear</Button>
          <Button onClick={exportImage} disabled={exporting || selectedRows.length === 0} data-testid="btn-export-image" className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-semibold">
            <ImageIcon size={14} className="mr-1" />{exporting ? "Exporting…" : "Export as Image"}
          </Button>
          <Button onClick={copyImage} disabled={copying || selectedRows.length === 0} data-testid="btn-copy-image" className="bg-amber-400 hover:bg-amber-300 text-slate-900 font-semibold">
            <Copy size={14} className="mr-1" />{copying ? "Copying…" : "Copy Image"}
          </Button>
        </div>
      </div>

      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-left text-xs uppercase text-muted-foreground">
                <th className="p-3 w-10">
                  <Checkbox
                    checked={filtered.length > 0 && selectedRows.length === filtered.length}
                    onCheckedChange={(v) => (v ? selectAll() : clearSel())}
                    data-testid="out-head-check"
                  />
                </th>
                <th className="p-3">Employee</th><th className="p-3">Phone</th>
                <th className="p-3 text-right">Outstanding</th>
                <th className="p-3 text-right">Unpaid Orders</th>
                <th className="p-3">Oldest Unpaid</th>
                <th className="p-3">Last Payment</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const ageDays = daysAgo(r.oldest_unpaid_date);
                const on = selected.has(r.id);
                return (
                  <tr key={r.id} className={`border-t table-row-hover ${on ? "bg-emerald-50 dark:bg-emerald-950/20" : ""}`} data-testid={`out-row-${r.id}`}>
                    <td className="p-3" onClick={(e) => e.stopPropagation()}>
                      <Checkbox checked={on} onCheckedChange={() => toggle(r.id)} data-testid={`out-check-${r.id}`} />
                    </td>
                    <td className="p-3 font-medium cursor-pointer" onClick={() => nav(`/employees/${r.id}`)}>{r.name}</td>
                    <td className="p-3 mono text-muted-foreground">{r.phone || "—"}</td>
                    <td className="p-3 text-right mono font-semibold text-rose-700">{formatIQD(r.outstanding)}</td>
                    <td className="p-3 text-right mono">{r.unpaid_orders_count}</td>
                    <td className="p-3">{r.oldest_unpaid_date ? <span>{formatDate(r.oldest_unpaid_date)} <span className="text-xs text-muted-foreground">({ageDays}d)</span></span> : "—"}</td>
                    <td className="p-3 text-muted-foreground">{r.last_payment_date ? formatDate(r.last_payment_date) : "—"}</td>
                  </tr>
                );
              })}
              {filtered.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">No employees with outstanding balances.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Offscreen export node */}
      <div style={{ position: "fixed", left: "-10000px", top: 0 }} aria-hidden>
        <div
          ref={exportRef}
          style={{
            width: "820px",
            fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
            padding: "32px",
            background: "#ffffff",
            color: "#0f172a",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", borderBottom: "2px solid #059669", paddingBottom: "16px", marginBottom: "20px" }}>
            <div>
              <div style={{ fontFamily: "'Manrope', system-ui, sans-serif", fontSize: "24px", fontWeight: 800, letterSpacing: "-0.02em" }}>Outstanding Payments</div>
              <div style={{ fontSize: "12px", color: "#64748b", marginTop: "4px" }}>Report generated {formatDate(todayISO())}</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ fontSize: "11px", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>MealLedger</div>
              <div style={{ fontSize: "11px", color: "#64748b" }}>Currency: IQD</div>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px", marginBottom: "20px" }}>
            <div style={{ background: "#f1f5f9", borderRadius: "12px", padding: "16px" }}>
              <div style={{ fontSize: "11px", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>Employees</div>
              <div style={{ fontFamily: "'Manrope', system-ui, sans-serif", fontSize: "28px", fontWeight: 800, marginTop: "4px" }}>{selectedRows.length}</div>
            </div>
            <div style={{ background: "#fef2f2", borderRadius: "12px", padding: "16px", border: "1px solid #fecaca" }}>
              <div style={{ fontSize: "11px", color: "#b91c1c", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>Total Outstanding</div>
              <div style={{ fontFamily: "'Manrope', system-ui, sans-serif", fontSize: "28px", fontWeight: 800, marginTop: "4px", color: "#b91c1c" }}>{formatIQD(selectedTotal)}</div>
            </div>
          </div>

          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
            <thead>
              <tr style={{ background: "#0f172a", color: "#fff" }}>
                <th style={{ padding: "10px 12px", textAlign: "left", fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>#</th>
                <th style={{ padding: "10px 12px", textAlign: "left", fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>Employee</th>
                <th style={{ padding: "10px 12px", textAlign: "right", fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>Outstanding</th>
                <th style={{ padding: "10px 12px", textAlign: "left", fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>Oldest Unpaid</th>
              </tr>
            </thead>
            <tbody>
              {selectedRows.map((r, i) => (
                <tr key={r.id} style={{ background: i % 2 === 0 ? "#ffffff" : "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                  <td style={{ padding: "10px 12px", color: "#64748b" }}>{i + 1}</td>
                  <td style={{ padding: "10px 12px", fontWeight: 600 }}>{r.name}</td>
                  <td style={{ padding: "10px 12px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, color: "#b91c1c" }}>{formatIQD(r.outstanding)}</td>
                  <td style={{ padding: "10px 12px", color: "#475569" }}>{r.oldest_unpaid_date ? `${formatDate(r.oldest_unpaid_date)}` : "—"}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: "2px solid #0f172a" }}>
                <td colSpan={2} style={{ padding: "12px", fontWeight: 700, textAlign: "right" }}>TOTAL</td>
                <td style={{ padding: "12px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", fontWeight: 800, fontSize: "16px", color: "#b91c1c" }}>{formatIQD(selectedTotal)}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>

          <div style={{ marginTop: "20px", fontSize: "11px", color: "#94a3b8", textAlign: "center" }}>
            Generated by MealLedger · {new Date().toLocaleString()}
          </div>
        </div>
      </div>

      {/* Reminder Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-3xl" data-testid="preview-dialog">
          <DialogHeader>
            <DialogTitle>Preview WhatsApp Reminders</DialogTitle>
            <DialogDescription>
              Threshold: outstanding &gt; {formatIQD(threshold)}. Uncheck anyone you want to exclude, then send.
            </DialogDescription>
          </DialogHeader>
          {previewLoading ? (
            <div className="p-6 text-center text-muted-foreground">Loading…</div>
          ) : (
            <div className="max-h-96 overflow-y-auto border border-border rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 sticky top-0">
                  <tr className="text-left text-xs uppercase text-muted-foreground">
                    <th className="p-2 w-8"></th><th className="p-2">Employee</th><th className="p-2">Phone</th><th className="p-2 text-right">Outstanding</th>
                  </tr>
                </thead>
                <tbody>
                  {candidates.map((c) => {
                    const include = !excluded.has(c.employee_id);
                    const noPhone = !c.phone;
                    return (
                      <tr key={c.employee_id} className={`border-t ${noPhone ? "bg-rose-50/50 dark:bg-rose-950/20" : ""}`} data-testid={`preview-row-${c.employee_id}`}>
                        <td className="p-2"><Checkbox checked={include} onCheckedChange={() => toggleExcluded(c.employee_id)} data-testid={`preview-check-${c.employee_id}`} disabled={noPhone} /></td>
                        <td className="p-2 font-medium">{c.name}</td>
                        <td className="p-2 mono text-xs">{c.phone || <span className="text-rose-700">No phone</span>}</td>
                        <td className="p-2 text-right mono font-semibold text-rose-700">{formatIQD(c.outstanding)}</td>
                      </tr>
                    );
                  })}
                  {candidates.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-muted-foreground">No candidates above threshold.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter className="!justify-between !items-center">
            <div className="text-sm text-muted-foreground">Will send to <strong data-testid="preview-count">{willSendCount}</strong> of {candidates.length}</div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setPreviewOpen(false)}>Cancel</Button>
              <Button onClick={sendReminders} disabled={sending || willSendCount === 0} data-testid="btn-confirm-send" className="bg-teal-600 hover:bg-teal-700">
                {sending ? "Sending…" : `Send to ${willSendCount}`}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
